  function myMeterHub(config) {
    return {
      dataUrl: config.dataUrl,
      meters: [],
      loading: true,
      showBackToTop: false,
      panelAlign: 'left',
      filtersExpanded: false,
      error: false,
      openGroup: null,
      searchQuery: '',
      searchOpen: false,
      showDiscontinued: false,
      discontinuedOnly: false,
      modelHelpOpen: false,
      zoomedImage: null,
      docsModal: { open: false, meter: null, type: 'manuals' },
      collectionProducts: [],
      collectionProductsLoading: false,
      collectionProductsError: false,
      activeFilters: {},
      filterGroups: [],

      async init() {
        try {
          const res = await fetch(this.dataUrl);
          if (!res.ok) throw new Error('Failed to fetch meter data');
          const data = await res.json();
          this.filterGroups = data.filterGroups || [];
          this.meters = data.meters || [];
          this.activeFilters = Object.fromEntries(this.filterGroups.map((g) => [g.id, null]));
        } catch (e) {
          console.error('My Meter hub: could not load data', e);
          this.error = true;
        } finally {
          this.loading = false;
        }

        this.updateHeaderOffset();
        window.addEventListener('resize', () => this.updateHeaderOffset());
      },

      // Measures the theme's real sticky header height at runtime and
      // exposes it as a CSS variable, so our own sticky toolbar sits
      // right below it instead of overlapping. Keystone doesn't expose
      // its own header height as a CSS variable (it only computes one
      // inside its own Alpine header component, which isn't reachable
      // from here), so we measure it ourselves the same way that
      // component does — via offsetHeight on the real <header> element.
      // Also measures our own search field's height, since on desktop
      // it's sticky too and the toolbar needs to stack directly below
      // it rather than overlapping.
      updateHeaderOffset() {
        const header = document.querySelector('header.sticky');
        if (header) {
          document.documentElement.style.setProperty('--mm-header-offset', header.offsetHeight + 'px');
        }
        const search = this.$refs.searchField;
        if (search) {
          document.documentElement.style.setProperty('--mm-search-height', search.offsetHeight + 'px');
        }
      },

      openDocs(meter, type) {
        this.docsModal = { open: true, meter, type };
        this.collectionProducts = [];
        this.collectionProductsError = false;

        if (type === 'parts' && meter.partsUrl) {
          this.fetchCollectionProducts(meter.partsUrl);
        }
      },

      // Pulls up to 4 live products from the meter's Parts & Accessories
      // collection using Shopify's public /products.json endpoint (no
      // API key needed — same data any storefront page can already see).
      async fetchCollectionProducts(collectionUrl) {
        this.collectionProductsLoading = true;
        try {
          const separator = collectionUrl.includes('?') ? '&' : '?';
          const res = await fetch(`${collectionUrl}/products.json${separator}limit=4`);
          if (!res.ok) throw new Error('Failed to fetch collection products');
          const data = await res.json();
          this.collectionProducts = (data.products || []).map((p) => ({
            title: p.title,
            url: `/products/${p.handle}`,
            image: p.images && p.images[0] ? p.images[0].src : null,
            price: p.variants && p.variants[0] && p.variants[0].price
              ? '$' + parseFloat(p.variants[0].price).toFixed(2)
              : null,
          }));
        } catch (e) {
          console.error('My Meter hub: could not load collection preview', e);
          this.collectionProductsError = true;
        } finally {
          this.collectionProductsLoading = false;
        }
      },

      // Determines the footer CTA for a meter card:
      // - Discontinued meters with a real best-replacement link get
      //   "Find Best Replacement" pointing at that replacement.
      // - Active meters get a URL-aware label ("View Product" for an
      //   actual /products/ page, "Visit Collection" otherwise) pointing
      //   at their productUrl.
      // - Returns null (hiding the link entirely) when there's genuinely
      //   nothing to point to — a placeholder "#", a missing URL, or a
      //   discontinued meter with no real replacement.
      footerLink(meter) {
        if (meter.discontinued) {
          const hasRealReplacement =
            meter.bestReplacementUrl &&
            meter.bestReplacementUrl !== '#' &&
            meter.bestReplacement &&
            !/^(n\/a|not available)$/i.test(meter.bestReplacement.trim());
          if (hasRealReplacement) {
            return { url: meter.bestReplacementUrl, label: 'Find Best Replacement \u2192' };
          }
          return null;
        }
        if (meter.productUrl && meter.productUrl !== '#') {
          const label = meter.productUrl.startsWith('/products/') ? 'View Product \u2192' : 'Visit Collection \u2192';
          return { url: meter.productUrl, label };
        }
        return null;
      },

      // Cascading filters: an option in one dimension is "available" if at
      // least one meter matches it AND the currently active filters in the
      // OTHER two dimensions (this dimension's own current selection is
      // ignored so it never locks itself out) AND the discontinued toggle.
      isOptionAvailable(groupId, opt) {
        if (this.activeFilters[groupId] === opt) return true;

        const otherGroupIds = Object.keys(this.activeFilters).filter((id) => id !== groupId);

        return this.meters.some((m) => {
          if (m[groupId] !== opt) return false;
          if (this.discontinuedOnly && !m.discontinued) return false;
          if (!this.discontinuedOnly && !this.showDiscontinued && m.discontinued) return false;
          return otherGroupIds.every((id) => {
            const selected = this.activeFilters[id];
            return selected === null || m[id] === selected;
          });
        });
      },

      selectFilter(groupId, value) {
        // Radio behavior: picking the already-active option clears the group
        // back to "all", picking a different one replaces the selection.
        this.activeFilters[groupId] = (this.activeFilters[groupId] === value) ? null : value;
        this.openGroup = null;
      },

      clearAll() {
        this.activeFilters = Object.fromEntries(this.filterGroups.map((g) => [g.id, null]));
        this.searchQuery = '';
        this.showDiscontinued = false;
        this.discontinuedOnly = false;
      },

      get hasActiveFilters() {
        return this.searchQuery.trim().length > 0 ||
          Object.values(this.activeFilters).some(value => value !== null) ||
          this.showDiscontinued || this.discontinuedOnly;
      },

      // Counts only what lives inside the collapsible filter section
      // (not search, which is a separate element outside it) — used for
      // the collapsed mobile bar's "N active" summary.
      get activeFilterCount() {
        let count = Object.values(this.activeFilters).filter(value => value !== null).length;
        if (this.showDiscontinued) count += 1;
        return count;
      },

      // Meters matching the dimension filters + discontinued toggle, before
      // search is applied. Shared by filteredMeters and searchSuggestions so
      // suggestions never point at something the active filters would hide.
      get dimensionFilteredMeters() {
        let result = this.meters;

        if (this.discontinuedOnly) {
          result = result.filter(m => m.discontinued);
        } else if (!this.showDiscontinued) {
          result = result.filter(m => !m.discontinued);
        }

        for (const groupId of Object.keys(this.activeFilters)) {
          const selected = this.activeFilters[groupId];
          if (selected !== null) {
            result = result.filter(m => m[groupId] === selected);
          }
        }

        return result;
      },

      get filteredMeters() {
        const q = this.searchQuery.trim().toLowerCase();
        if (!q) return this.dimensionFilteredMeters;

        return this.dimensionFilteredMeters.filter(
          m => m.model.toLowerCase().includes(q) || (m.series || '').toLowerCase().includes(q)
        );
      },

      // Predictive dropdown: up to 6 title matches from whatever the active
      // filters currently allow, so clicking a suggestion never produces an
      // empty grid.
      get searchSuggestions() {
        const q = this.searchQuery.trim().toLowerCase();
        if (!q) return [];

        return this.dimensionFilteredMeters
          .filter(m => m.model.toLowerCase().includes(q))
          .slice(0, 6);
      },
    };
  }
